import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { pledgeFrequencySchema, pledgeMethodSchema, pledgeStatusSchema } from './portal.js';

const pledgeErrors = {
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
};

export const pledgeChargeStateSchema = z.enum(['scheduled', 'pending', 'succeeded', 'abandoned']);

export const pledgeChargeSchema = z.object({
  id: z.uuid(),
  dueDate: z.string(),
  status: pledgeChargeStateSchema,
  attemptCount: z.number().int(),
  nextAttemptAt: z.string().nullable(),
  amount: moneySchema,
  failureCode: z.string().nullable(),
  failureKind: z.enum(['soft', 'hard', 'mandate', 'unavailable']).nullable(),
  requestedAt: z.string().nullable(),
  settledAt: z.string().nullable(),
});

export type PledgeChargeDto = z.infer<typeof pledgeChargeSchema>;

/**
 * An operator's view of a standing gift. It names the organization and the
 * donor, which the donor's own view never does, and carries the failure the
 * follow-up screen exists for.
 */
export const adminPledgeSchema = z.object({
  id: z.uuid(),
  status: pledgeStatusSchema,
  organizationId: z.uuid(),
  organizationCode: z.string(),
  organizationName: z.string(),
  donorId: z.uuid().nullable(),
  donorName: z.string().nullable(),
  campaignId: z.uuid(),
  campaignName: z.string(),
  campaignSlug: z.string(),
  amount: moneySchema,
  quantity: z.number().int().nullable(),
  frequency: pledgeFrequencySchema,
  dayOfMonth: z.number().int(),
  startsOn: z.string(),
  nextDueOn: z.string().nullable(),
  lastChargedOn: z.string().nullable(),
  beneficiaryRef: z.string().nullable(),
  consecutiveFailures: z.number().int(),
  cancelReason: z.string().nullable(),
  method: pledgeMethodSchema,
  createdAt: z.string(),
});

export type AdminPledge = z.infer<typeof adminPledgeSchema>;

/**
 * §6.4's pledge oversight and failed-collection follow-up —
 * docs/plans/06-portal-and-field.md.
 *
 * **"Sponsors of this beneficiary" is `beneficiaryRef` on this list**, not a
 * screen of its own: the answer is a set of rows the list already renders, so
 * it costs a filter rather than a route and the operator can page, sort and
 * share it like any other view (`.claude/rules/list-screen-pattern.md`,
 * *A filter can be a report*).
 *
 * Every read is **organization scope**, like every other row that sits outside
 * the geographic tree (docs/decisions/0045).
 */
export const pledgeContract = {
  list: oc
    .route({ method: 'GET', path: '/pledges' })
    .errors(pledgeErrors)
    .input(
      paginationInputSchema.extend({
        organizationId: z.uuid().optional(),
        campaignId: z.uuid().optional(),
        status: pledgeStatusSchema.optional(),
        beneficiaryRef: z.string().trim().min(1).max(120).optional(),
      }),
    )
    .output(paginated(adminPledgeSchema)),

  get: oc
    .route({ method: 'GET', path: '/pledges/{id}' })
    .errors(pledgeErrors)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ pledge: adminPledgeSchema, charges: z.array(pledgeChargeSchema) })),

  pause: oc
    .route({ method: 'POST', path: '/pledges/{id}/pause' })
    .errors(pledgeErrors)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ pledge: adminPledgeSchema })),

  resume: oc
    .route({ method: 'POST', path: '/pledges/{id}/resume' })
    .errors(pledgeErrors)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ pledge: adminPledgeSchema })),

  /** A reason is required: stopping somebody else's standing gift is a decision. */
  cancel: oc
    .route({ method: 'POST', path: '/pledges/{id}/cancel' })
    .errors(pledgeErrors)
    .input(z.object({ id: z.uuid(), reason: z.string().trim().min(3).max(500) }))
    .output(z.object({ pledge: adminPledgeSchema })),
};
